/**
 * Single source of truth for the Atlas AI agent_type vocabulary used by
 * console-side AGENT_OPTIONS dropdowns and pattern-table labels.
 *
 * See: ADR-0081 + atlas-meta/ai/KNOWLEDGE_LAYER.md.
 *
 * `echarts` and `query_test` are deprecated and intentionally NOT in
 * AGENT_OPTIONS — historical rows still using those values render via the
 * AGENT_LABEL fallback (raw value) at the call site.
 */

export type AgentType =
  | "core"
  | "query"
  | "tantable"
  | "kpi"
  | "reactive_viz"
  | "transform"
  | "architect"
  | "ai_provider"
  | "workbench"
  | "door";

export const AGENT_OPTIONS: ReadonlyArray<{
  value: AgentType;
  label: string;
  description: string;
}> = [
  {
    value: "core",
    label: "Core (every agent)",
    description: "Universal principles loaded by every AI agent.",
  },
  {
    value: "query",
    label: "Atlas Query",
    description: "SQL authoring assistant for the Atlas Query surface.",
  },
  {
    value: "tantable",
    label: "TanTable",
    description: "Tabular spec authoring (groupBy, measures, pivot, format).",
  },
  {
    value: "kpi",
    label: "KPI Card",
    description: "KPI card item authoring.",
  },
  {
    value: "reactive_viz",
    label: "Reactive Viz",
    description: "Reactive visualization (SQL + typed-spec chart in one node).",
  },
  {
    value: "transform",
    label: "Transform (DuckDB SQL)",
    description: "Transform-node SQL authoring.",
  },
  {
    value: "architect",
    label: "Pipeline Architect",
    description: "End-to-end pipeline graph authoring.",
  },
  {
    value: "ai_provider",
    label: "AI Provider Node",
    description: "AI Provider node prompt + structured-output authoring.",
  },
  {
    value: "workbench",
    label: "Knowledge Workbench",
    description: "Meta-agent that authors patterns + eval cases.",
  },
  {
    value: "door",
    label: "Door (external agents — Claude, Copilot)",
    description:
      "Conduct for external agents on the MCP door — never loaded by Atlas's own agents.",
  },
];

/**
 * Label lookup for rendering an agent_type in the pattern table. Historical
 * rows tagged with deprecated values (`echarts`, `query_test`) fall through
 * to the raw value via the call-site `?? p.agent_type` pattern.
 */
export const AGENT_LABEL: Record<string, string> = Object.fromEntries(
  AGENT_OPTIONS.map((a) => [a.value, a.label])
);

/**
 * ADR-0154 §6.3 C3 — label for a SERVED agent type.
 *
 * `AGENT_OPTIONS` above is a presentation overlay, not a roster: the hub owns
 * the agent-type vocabulary and serves it (`agent_types` on the knowledge-meta
 * payloads), and four console surfaces already consume the served list. The
 * one that did not was missing `model_advisor`, so an entire agent type was
 * unselectable there. Anything absent from the overlay is humanized rather
 * than dropped, which is what stops that from recurring.
 */
export function agentTypeLabel(agentType: string): string {
  return AGENT_LABEL[agentType] ?? agentType.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}
